import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Facebook, Linkedin, Instagram } from 'lucide-react';
import { siteConfig } from '../data';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Ensure website_url is present (it should be from FormData, but let's be explicit)
    if (!data.hasOwnProperty('website_url')) {
      data.website_url = "";
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        navigate('/tak');
      } else {
        let errorMessage = 'Der skete en fejl ved indsendelse. Prøv venligst igen senere.';
        try {
          const errorData = await response.json();
          console.error('Server error details:', errorData);
          if (errorData.message) errorMessage = errorData.message;
        } catch (e) {
          console.error('Could not parse error response as JSON', e);
        }
        
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Webform submission error:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <section className="py-12 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-6">Kontakt mig</h1>
            <p className="text-xl text-slate-600">
              Har du spørgsmål til et forløb, et foredrag eller en workshop? Jeg sidder klar til at hjælpe dig videre.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Contact Info (Col-6) */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">Lad os tage en snak</h2>
                <p className="text-lg text-slate-600 mb-10 leading-relaxed">
                  Jeg tror på den gode samtale som udgangspunkt for ethvert samarbejde. Kontakt mig i dag, så finder vi ud af, hvordan jeg bedst kan hjælpe dig.
                </p>
              </div>

              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="bg-emerald-900 p-4 shrink-0">
                    <Mail className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 text-lg mb-1">Send en mail</h4>
                    <p className="text-slate-600 text-lg">{siteConfig.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-6">
                  <div className="bg-emerald-900 p-4 shrink-0">
                    <Phone className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 text-lg mb-1">Ring til mig</h4>
                    <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} className="text-slate-600 text-lg hover:text-emerald-700 transition-colors">
                      {siteConfig.phone}
                    </a>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-zinc-100">
                  <h4 className="font-bold text-zinc-900 text-lg mb-4">Følg med på sociale medier</h4>
                  <div className="flex items-center space-x-4">
                    {siteConfig.social.facebook && (
                      <a 
                        href={siteConfig.social.facebook} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="bg-zinc-100 hover:bg-emerald-900 p-3 text-zinc-700 hover:text-white transition-all"
                        aria-label="Facebook"
                      >
                        <Facebook className="h-5 w-5" />
                      </a>
                    )}
                    {siteConfig.social.linkedin && (
                      <a 
                        href={siteConfig.social.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="bg-zinc-100 hover:bg-emerald-900 p-3 text-zinc-700 hover:text-white transition-all"
                        aria-label="LinkedIn"
                      >
                        <Linkedin className="h-5 w-5" />
                      </a>
                    )}
                    {siteConfig.social.instagram && (
                      <a 
                        href={siteConfig.social.instagram} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="bg-zinc-100 hover:bg-emerald-900 p-3 text-zinc-700 hover:text-white transition-all"
                        aria-label="Instagram"
                      >
                        <Instagram className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-8 bg-emerald-950 text-white shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <CheckCircle className="h-32 w-32" />
                </div>
                <div className="relative z-10">
                  <h3 className="text-xl font-bold mb-4">Book en gratis og uforpligtende samtale</h3>
                  <p className="text-emerald-50/70 mb-6 leading-relaxed">
                    Jeg tilbyder altid en gratis og åben samtale, hvor fokus er på at matche dine behov og mine erfaringer – helt uden fastlagt tidsramme.
                  </p>
                  <div className="flex items-center text-white font-bold">
                    <CheckCircle className="h-5 w-5 mr-2 text-white" />
                    <span>Svar indenfor 24 timer</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Form (Col-6) */}
            <div className="bg-white p-8 md:p-12 border border-zinc-200 shadow-2xl relative lg:mt-0">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot - Mandatory for anti-spam */}
                <div className="sr-only" aria-hidden="true">
                  <label htmlFor="website_url">Website URL</label>
                  <input 
                    type="text" 
                    id="website_url" 
                    name="website_url" 
                    tabIndex="-1" 
                    autoComplete="off" 
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-emerald-900 ml-1">Navn</label>
                    <input
                      required
                      type="text"
                      name="name"
                      placeholder="Dit fulde navn"
                      className="w-full px-5 py-4 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-900 transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-emerald-900 ml-1">E-mail</label>
                    <input
                      required
                      type="email"
                      name="email"
                      placeholder="din@mail.dk"
                      className="w-full px-5 py-4 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-900 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-emerald-900 ml-1">Virksomhed (valgfri)</label>
                  <input
                    type="text"
                    name="company"
                    placeholder="Evt. virksomhedsnavn"
                    className="w-full px-5 py-4 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-900 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-emerald-900 ml-1">Emne</label>
                  <select
                    name="subject"
                    className="w-full px-5 py-4 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-900 transition-all bg-white"
                  >
                    <option value="coaching">1:1 Coaching</option>
                    <option value="foredrag">Foredrag</option>
                    <option value="workshop">Workshops & Temadage</option>
                    <option value="andet">Andet</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-emerald-900 ml-1">Besked</label>
                  <textarea
                    required
                    name="message"
                    rows="5"
                    placeholder="Fortæl lidt om hvad du har brug for hjælp til..."
                    className="w-full px-5 py-4 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-900 transition-all"
                  ></textarea>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 text-red-600 text-sm font-medium">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-900 text-white py-5 font-bold text-lg hover:bg-emerald-950 transition-all flex items-center justify-center shadow-lg disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sender besked...
                    </span>
                  ) : (
                    <>
                      Send besked
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
